import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subscription, switchMap } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { EntityDetailsService } from './entity-details.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-entity-details',
    templateUrl: './entity-details.component.html',
    styleUrls: ['./entity-details.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UpdateEntityDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  /** False until we have grid row + filters and actually start `initializeData` (avoids infinite spinner before Filter). */
  loading = false;
  crudLoading: boolean = false;
  minimise: boolean = false;

  // Form data
  entityDetailsForm: UntypedFormGroup;
  selected8858Entity: any = null;
  selectedFormFiler: any = null;
  selectedClassification: any = null;
  selectedDatsmApplicable: any = null;
  selectedFSEFlag: any = null;
  selectedGiltiTaxType: any = null;
  selectedGiltiCGK: any = null;

  // Dropdown options
  entities_8858: any[] = [];
  filerIndices: any[] = [];
  fseIndices: any[] = [];
  entityClassification: any[] = [];
  datsmIndices: any[] = [];
  giltiTaxType: any[] = [];
  giltiConsolGroupKey: any[] = [];
  object_id: any[] = [];

  // Conditional display flags
  showInput: boolean = true;
  showInputClass: boolean = true;
  showInput8858: boolean = true;
  showInput5471: boolean = true;
  showInputGiltiTaxType: boolean = true;
  showInputGiltiCGK: boolean = true;
  showFSEFlag: boolean = false;

  // Autocomplete for 8858 entities
  entity8858Control = new UntypedFormControl();
  filtered8858Entities: Observable<any[]> = of([]);
  loading8858Accounts: boolean = false;
  noResults8858: boolean = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private entityDetailsService: EntityDetailsService
  ) {
    this.entityDetailsForm = this.fb.group({
      formFiler: [''],
      entityClassification: [''],
      datsmApplicable: [''],
      entity8858: [''],
      giltiTaxType: [''],
      giltiCGK: [''],
      fseFlag: ['']
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));

    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  init(): void {
    // Following migration pattern - check if all required data is available
    const ready =
      this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
      this.filterParams && Object.keys(this.filterParams).length > 0 &&
      this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
      this.rowData && Object.keys(this.rowData).length > 0 &&
      !!this.resolveApiBase();

    if (ready) {
      this.initializeData();
    } else {
      this.loading = false;
    }
  }

  initializeData(): void {
    this.loading = true;

    // Load all dropdown data
    this.load8858Entities();
    this.loadFormFiler();
    this.loadEntityClassification();
    this.loadDatsmApplicable();
    this.loadGiltiTaxType();
    this.loadGiltiCGK();
    this.loadFSEFlag();

    // Set up autocomplete for 8858 entities
    this.setup8858Autocomplete();

    // Set conditional display flags based on rowData
    this.setConditionalDisplayFlags();

    this.loading = false;
  }

  setup8858Autocomplete(): void {
    // Setup autocomplete with debounce and API call
    this.filtered8858Entities = this.entity8858Control.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: any) => {
        // 🚀 CRITICAL: If value is already an object (selected entity), don't search
        // Just return empty array to keep the selected value displayed
        if (value && typeof value === 'object' && value.DISPLAY_LABEL) {
          this.loading8858Accounts = false;
          // Return the selected entity in an array so it can be displayed
          return of([value]);
        }

        // Handle string input for searching
        const searchString = typeof value === 'string' ? value : '';

        if (searchString && searchString.length >= 3) {
          this.loading8858Accounts = true;
          this.noResults8858 = false;
          return this.entityDetailsService.get8858Entities(
            this.resolveApiBase(),
            { search_string: searchString },
            this.globalService,
            this.filterParams,
            this.rowData
          ).pipe(
            map((response: any) => {
              this.loading8858Accounts = false;
              if (response.callSuccess === "1" && response.jsonObject) {
                this.noResults8858 = response.jsonObject.length === 0;
                return response.jsonObject;
              } else {
                this.noResults8858 = true;
                return [];
              }
            }),
            catchError((error) => {
              this.loading8858Accounts = false;
              this.noResults8858 = true;
              console.error('Error loading 8858 entities:', error);
              return of([]);
            })
          );
        } else {
          this.loading8858Accounts = false;
          return of([]);
        }
      })
    );
  }

  display8858Entity(entity: any): string {
    // 🚀 CRITICAL: Handle both object and string/null values
    if (!entity) {
      return '';
    }
    if (typeof entity === 'object' && entity.DISPLAY_LABEL) {
      return entity.DISPLAY_LABEL;
    }
    if (typeof entity === 'string') {
      return entity;
    }
    return '';
  }

  on8858EntitySelected(event: any): void {
    // 🚀 CRITICAL: optionSelected event - Angular Material already sets the form control value
    // We just need to update our selected8858Entity property
    const entity = event.option?.value || event.value || event;

    if (entity && typeof entity === 'object') {
      this.selected8858Entity = entity;
      // Note: Angular Material autocomplete automatically sets the form control value
      // and calls displayWith function, so we don't need to set it manually
    } else {
      this.selected8858Entity = null;
    }
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  setConditionalDisplayFlags(): void {
    if (this.rowData.TYPE === 'CCLE' || this.rowData.TYPE === 'CCBR') {
      this.showInput = false;
    }

    if (this.rowData.TYPE === 'CCLE' || this.rowData.TYPE === 'CCBR') {
      this.showInputClass = false;
    }

    if (this.rowData.TYPE === 'CCLE' || this.rowData.TYPE === 'HO') {
      this.showInput8858 = false;
    }

    if (this.rowData.TAX_TYPE !== 'CFC' && this.rowData.TAX_TYPE !== 'NCFC') {
      this.showInput5471 = false;
    }

    if (this.rowData.TAX_TYPE !== 'CFC' && this.rowData.TAX_TYPE !== 'NCFC' && this.rowData.TYPE === 'HO') {
      this.showInputGiltiTaxType = false;
      this.showInputGiltiCGK = false;
    }

    if ((this.rowData.TAX_TYPE === 'CFC' || this.rowData.TAX_TYPE === 'NCFC') && this.rowData.TYPE === 'HO') {
      this.showFSEFlag = true;
    }
  }

  load8858Entities(): void {
    this.entityDetailsService.get8858Entities(
      this.resolveApiBase(),
      {},
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess !== "1") {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.entities_8858 = response.jsonObject || [];
        }
      },
      (error: any) => {
        console.error('Error loading 8858 entities:', error);
        this.gtwSnackbarService.openSnackBar('Error loading 8858 entities', 'gtw-snackbar--red');
      }
    );
  }

  loadFormFiler(): void {
    this.filerIndices = [
      {label: "Y", value: "Y"},
      {label: "N", value: "N"}
    ];
  }

  loadFSEFlag(): void {
    this.fseIndices = [
      {label: "Y", value: "Y"},
      {label: "N", value: "N"}
    ];
  }

  loadEntityClassification(): void {
    this.entityClassification = [
      {label: "CMPX", value: "CMPX"},
      {label: "DMNT", value: "DMNT"}
    ];
  }

  loadGiltiTaxType(): void {
    this.giltiTaxType = [
      {label: "CFC", value: "CFC"},
      {label: "NCFC", value: "NCFC"}
    ];
  }

  loadGiltiCGK(): void {
    this.entityDetailsService.getGiltiCGK(
      this.resolveApiBase(),
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess !== "1") {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.giltiConsolGroupKey = response.jsonObject || [];
        }
      },
      (error: any) => {
        console.error('Error loading GILTI CGK:', error);
        this.gtwSnackbarService.openSnackBar('Error loading GILTI Consol Group Key', 'gtw-snackbar--red');
      }
    );
  }

  loadDatsmApplicable(): void {
    this.selectedDatsmApplicable = this.rowData.DATSM_APPLICABLE || 'N';
    this.datsmIndices = [
      {label: "Y", value: "Y"},
      {label: "N", value: "N"}
    ];
  }

  resetDetails(): void {
    this.crudLoading = false;
    this.selected8858Entity = null;
    this.selectedFormFiler = null;
    this.selectedClassification = null;
    this.selectedFSEFlag = null;
    this.selectedDatsmApplicable = this.rowData.DATSM_APPLICABLE || 'N';
    this.selectedGiltiTaxType = null;
    this.selectedGiltiCGK = null;
    this.entity8858Control.setValue('', { emitEvent: false });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    // const updateEvent = new CustomEvent('request-grid-update', {
    //   detail: 'cancel',
    //   bubbles: true
    // });

    // if (document.querySelector('gtw-entity-details-ce')) {
    //   document.querySelector('gtw-entity-details-ce')?.dispatchEvent(updateEvent);
    // }
  }

  refreshGrid(): void {
    // 🚀 CRITICAL: Emit 'refresh' string for AngularJS modal close
    this.requestGridUpdate.emit('refresh');

    // 🚀 CRITICAL: Dispatch CustomEvent for AngularJS integration with 'refresh' detail
    // Following pattern from f163j-schd-a-data-refresh, f1120-sch-m2, fir-5471-schedule-m
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'refresh',  // 🚀 CRITICAL: Must be 'refresh' not 'cancel' to trigger grid refresh
      bubbles: true
    });

    if (document.querySelector('gtw-entity-details-ce')) {
      document.querySelector('gtw-entity-details-ce')?.dispatchEvent(updateEvent);
    }
  }

  saveDetails(): void {
    // Validation
    if (((this.selected8858Entity === undefined || this.selected8858Entity === null || this.selected8858Entity === '') &&
        (this.selectedFormFiler === undefined || this.selectedFormFiler === null) &&
        (this.selectedClassification === undefined || this.selectedClassification === null) &&
        (this.selectedGiltiTaxType === undefined || this.selectedGiltiTaxType === null) &&
        (this.selectedGiltiCGK === undefined || this.selectedGiltiCGK === null)
        ) && (this.showInput5471 === false)
       ) {
      this.gtwSnackbarService.openSnackBar('Select atleast one input', 'gtw-snackbar--red');
      return;
    }

    const params = {
      le_key_8858: (this.selected8858Entity) ? this.selected8858Entity.DD_VALUE : "",
      form_filer: (this.selectedFormFiler) ? this.selectedFormFiler.value : "",
      entity_classification: (this.selectedClassification) ? this.selectedClassification.value : "",
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      reporting_period: this.rowData.REPORTING_PERIOD,
      le_key: this.rowData.LE_KEY,
      consol_group_key: this.rowData.CONSOL_GROUP_KEY,
      object_id: this.rowData.object_id,
      datsm_applicable: (this.selectedDatsmApplicable) ? this.selectedDatsmApplicable : 'N',
      gilti_tax_type_override: (this.selectedGiltiTaxType) ? this.selectedGiltiTaxType.value : null,
      gilti_consol_group_key_override: (this.selectedGiltiCGK) ? this.selectedGiltiCGK.VALUE : null,
      fse_flag: (this.selectedFSEFlag) ? this.selectedFSEFlag.value : ""
    };

    this.crudLoading = true;
    this.object_id.push({ OBJECT_ID: this.rowData.OBJECT_ID });

    this.entityDetailsService.updateEntityDetails(
      this.resolveApiBase(),
      params,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        if (response.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully', 'gtw-snackbar--green');
          this.refreshGrid();
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        this.crudLoading = false;
        console.error('Error saving entity details:', error);
        this.gtwSnackbarService.openSnackBar('Error saving data. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}

